import type { EventEmitter } from 'events';
import qs from 'qs';
import type { AnalyticsConfig, IntegrationConfig } from './interfaces';
import { debugLog } from './utils/debug';
import { encodeConsentToken, getRelayableConsent } from './utils/consentRelay';
import {
  type GoogleParams,
  getGoogleSessionParams,
  withGoogleAdsClick
} from './utils/googleAnalytics';
import { removeQueryParamPreservingEncoding } from './utils/url';

export interface RedirectConfig {
  integration: IntegrationConfig;
  analytics: AnalyticsConfig;
  lng?: string;
  hotelId?: string;
}

interface RedirectArgs {
  config: RedirectConfig;
  dbmBaseUrl: string;
  params?: any;
  triggerEventEmitter?: any;
  enabledThirdPartyCookies?: boolean;
}

/**
 * Takes an object and returns an object without keys that have falsy values.
 * @param {Object} obj object to remove the falsy key/values from.
 * @returns {Object} the object without the falsy values.
 */
const filterFalsy = (obj: any) =>
  Object.keys(obj)
    .filter((x) => !!obj[x])
    .reduce((acc, x) => ({ ...acc, [x]: obj[x] }), {});

export async function redirect({
  config,
  params = {},
  dbmBaseUrl,
  triggerEventEmitter,
  enabledThirdPartyCookies = true
}: RedirectArgs) {
  let redirectUrl = dbmBaseUrl;
  const { rooms, rates, ...searchParams } = params;
  delete searchParams.gcs;
  delete searchParams.evoIbePreview;
  const useEvo = config.integration?.useEvo === true;
  const effectiveHotelId = searchParams.hotel || config.hotelId;

  if (useEvo) {
    if (rooms) searchParams.roomIds = rooms;
    if (rates) searchParams.rateIds = rates;

    if (effectiveHotelId && effectiveHotelId.indexOf(';') === -1) {
      redirectUrl += `/${effectiveHotelId}/availabilities`;
    }
  } else {
    if (rooms) searchParams.filters = { rooms };
    if (rates) searchParams.filters = { ...searchParams.filters, rates };

    if (effectiveHotelId && effectiveHotelId.indexOf(';') === -1) {
      redirectUrl += '/availability';
    }
  }

  let googleSessionParams: any;
  if (config.analytics.gtmContainerId) {
    googleSessionParams = (await getGoogleSessionParams(config.analytics)) as GoogleParams;
  }

  const pageParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  delete pageParams.gcs;
  delete pageParams.evoIbePreview;
  const otherParams = config.analytics.gtmContainerId
    ? withGoogleAdsClick(redirectUrl, pageParams, Boolean(googleSessionParams?.clientId))
    : pageParams;
  const consent =
    useEvo && config.analytics.gtmContainerId ? getRelayableConsent() : null;

  const baseParams = useEvo
    ? {
        ...searchParams,
        ...(googleSessionParams || {}),
        ...otherParams,
        lng: searchParams.lng || config.lng
      }
    : {
        ...searchParams,
        ...(googleSessionParams || {}),
        ...otherParams,
        lng: searchParams.lng || config.lng,
        hotel: effectiveHotelId
      };

  const serializedParams = qs.stringify(baseParams, {
    encode: false,
    addQueryPrefix: true,
    indices: !useEvo
  });
  const paramsWithoutConsent = removeQueryParamPreservingEncoding(serializedParams, 'gcs');
  const allParams = consent
    ? `${paramsWithoutConsent}${paramsWithoutConsent ? '&' : '?'}gcs=${encodeConsentToken(consent)}`
    : paramsWithoutConsent;

  redirectUrl = `${redirectUrl}${allParams}`;

  debugLog('Redirect url', dbmBaseUrl, searchParams);

  if (
    triggerEventEmitter &&
    config.integration &&
    config.integration.panelEnabled &&
    enabledThirdPartyCookies
  ) {
    triggerEventEmitter.emit('panel', { show: true, redirectUrl });
  } else if (config.integration && config.integration.newTabRedirect) {
    window.open(redirectUrl, '_blank');
  } else {
    window.location.assign(redirectUrl);
  }
}

export function getParentByDataAttribute(
  element: Element,
  attribute: string,
  value?: string
): Element | null {
  let elementWithAttribute = null;

  if (element && element.hasAttribute) {
    if (element.hasAttribute(attribute)) {
      if (value === undefined) {
        elementWithAttribute = element;
      } else if (element.getAttribute(attribute) === value) {
        elementWithAttribute = element;
      }
    }

    if (!elementWithAttribute) {
      elementWithAttribute = getParentByDataAttribute(
        element.parentNode as Element,
        attribute,
        value
      );
    }
  }

  return elementWithAttribute;
}

export default function (
  config: RedirectConfig,
  dbmBaseUrl: string,
  triggerEventEmitter: EventEmitter
) {
  window.addEventListener(
    'click',
    (event) => {
      const redirectElement = getParentByDataAttribute(
        event.target as Element,
        'data-guestline-redirect'
      );

      triggerEventEmitter.emit('clicked', event);

      if (redirectElement) {
        // So we stop OBM from happening
        event.stopPropagation();
        event.preventDefault();

        const params = filterFalsy({
          hotel: redirectElement.getAttribute('data-hotel-id'),
          arrival: redirectElement.getAttribute('data-arrival'),
          departure: redirectElement.getAttribute('data-departure'),
          nights: redirectElement.getAttribute('data-nights'),
          adults: redirectElement.getAttribute('data-adults'),
          children: redirectElement.getAttribute('data-children'),
          promoCode: redirectElement.getAttribute('data-promo-code'),
          rates: redirectElement.getAttribute('data-rates')?.split(';') ?? [],
          rooms: redirectElement.getAttribute('data-rooms')?.split(';') ?? [],
          lng: redirectElement.getAttribute('data-lng') || config.lng
        });

        redirect({
          config,
          triggerEventEmitter,
          dbmBaseUrl,
          params
        });
      }
    },
    true
  );

  window.addEventListener(
    'submit',
    (event: Event) => {
      if ((event.target as HTMLFormElement).hasAttribute('data-guestline-form')) {
        // So we stop OBM from happening
        event.stopPropagation();
        event.preventDefault();

        const formElements: any = (event.target as HTMLFormElement).elements;
        const params = filterFalsy({
          hotel: (formElements['hotel-id'] || {}).value,
          arrival: (formElements['arrival'] || {}).value,
          departure: (formElements['departure'] || {}).value,
          nights: (formElements['nights'] || {}).value,
          adults: (formElements['adults'] || {}).value,
          children: (formElements['children'] || {}).value,
          promoCode: (formElements['promo-code'] || {}).value,
          rates: (formElements['rates'] || {}).value?.split(';') ?? [],
          rooms: (formElements['rooms'] || {}).value?.split(';') ?? [],
          lng: (formElements['lng'] || {}).value || config.lng
        });

        redirect({ config, triggerEventEmitter, dbmBaseUrl, params });
      }
    },
    true
  );
}
